import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, ExternalLink, RefreshCw, Video } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const JITSI_DOMAIN = "meet.jit.si";

export function meetingRoomFor(appointment) {
  if (!appointment) return null;
  return appointment.meeting_room || `healthcircles-appt-${appointment.id}`;
}

/** Public patient URL for sharing (works without token). */
export function meetingLinkFor(appointment) {
  if (!appointment) return null;
  if (appointment.meeting_url) return appointment.meeting_url;
  const room = meetingRoomFor(appointment);
  return room ? `https://${JITSI_DOMAIN}/${room}` : null;
}

export default function VideoCallDialog({ open, onOpenChange, appointment, doctorName, onRoomChange }) {
  const iframeRef = useRef(null);
  const [embedUrl, setEmbedUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const provider = appointment?.meeting_provider === "daily" || (appointment?.meeting_url || "").includes(".daily.co")
    ? "daily" : "jitsi";
  const patientLink = meetingLinkFor(appointment);

  useEffect(() => {
    if (!open || !appointment) { setEmbedUrl(""); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (provider === "daily") {
          // Mint a fresh owner token for the doctor and embed via query param
          let token = null;
          try {
            const { data } = await api.post(`/appointments/${appointment.id}/meeting/doctor-token`);
            token = data?.token || null;
          } catch (_) {}
          if (cancelled) return;
          const base = appointment.meeting_url;
          const name = encodeURIComponent(doctorName || "Doctor");
          const url = token ? `${base}?t=${token}&userName=${name}` : `${base}?userName=${name}`;
          setEmbedUrl(url);
        } else {
          const room = meetingRoomFor(appointment);
          const name = encodeURIComponent(doctorName || "Doctor");
          // Jitsi accepts #userInfo.displayName
          setEmbedUrl(`https://${JITSI_DOMAIN}/${room}#userInfo.displayName=%22${name}%22`);
        }
      } catch (e) {
        console.error(e);
        toast.error("Could not prepare video call.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, appointment, provider, doctorName]);

  const copyLink = async () => {
    if (!patientLink) return;
    await navigator.clipboard.writeText(patientLink);
    toast.success("Meeting link copied");
  };

  const regenerate = async () => {
    if (!appointment?.id) return;
    try {
      const { data } = await api.post(`/appointments/${appointment.id}/meeting/regenerate`);
      onRoomChange?.(data);
      toast.success("New meeting link generated");
    } catch (e) { toast.error("Failed to regenerate"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[24px] max-w-5xl p-0 overflow-hidden" data-testid="video-call-dialog">
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="text-lg text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>
            Remote consultation {appointment?.patient_name ? `— ${appointment.patient_name}` : ""}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6C7B72]">
            Powered by {provider === "daily" ? "Daily.co (healthcircles.daily.co)" : "Jitsi Meet"}. Share the link with your patient so they can join.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[220px] text-xs font-mono bg-[#F3F1EC] rounded-lg px-3 py-2 break-all text-[#1F2A24]" data-testid="meeting-link-text">{patientLink || "—"}</div>
          <button className="btn-outline-emerald" onClick={copyLink} data-testid="meeting-copy-btn"><Copy size={14} /> Share meeting link</button>
          <a className="btn-outline-emerald" href={patientLink} target="_blank" rel="noreferrer" data-testid="meeting-open-tab"><ExternalLink size={14} /> Open in new tab</a>
          <button className="btn-peach" onClick={regenerate} data-testid="meeting-regenerate"><RefreshCw size={14} /> Regenerate</button>
        </div>

        <div className="relative bg-[#0A4C36]" style={{ height: 560 }}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm gap-2 z-10 pointer-events-none">
              <Video size={18} /> Loading video call…
            </div>
          )}
          {embedUrl && (
            <iframe
              ref={iframeRef}
              src={embedUrl}
              title="Video consultation"
              allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-read; clipboard-write"
              className="w-full h-full border-0"
              data-testid="video-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
